import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchContent } from "@/lib/content";

interface SearchBoxProps {
  className?: string;
  onNavigate?: () => void;
}

export default function SearchBox({ className = "", onNavigate }: SearchBoxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  const result = useMemo(() => searchContent(query), [query]);
  const hasResult = result.dramas.length > 0 || result.actors.length > 0;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const goTo = (path: string) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(path);
  };

  const submitSearch = () => {
    if (!query.trim()) return;
    goTo(`/category?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 transition-colors focus-within:border-primary-400">
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-search-line text-foreground-400" />
        </span>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitSearch();
          }}
          placeholder="搜索剧集 / 演员..."
          aria-label="搜索剧集或演员"
          className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
        />
      </div>

      {open && query.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-80 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg">
          {hasResult ? (
            <>
              {result.dramas.length > 0 && (
                <div className="mb-1">
                  <p className="px-2 py-1 text-xs font-medium text-foreground-400">
                    剧集
                  </p>
                  {result.dramas.slice(0, 5).map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => goTo(`/detail/${d.id}`)}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="block h-11 w-8 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={d.poster}
                          alt={d.title}
                          title={`${d.title} 剧集海报`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground-900">
                          {d.title}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">
                          {d.year} · {d.genre}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {result.actors.length > 0 && (
                <div>
                  <p className="px-2 py-1 text-xs font-medium text-foreground-400">
                    演员
                  </p>
                  {result.actors.slice(0, 5).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => goTo(`/actor/${a.id}`)}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="block h-10 w-10 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img
                          src={a.avatar}
                          alt={a.name}
                          title={`${a.name} 演员头像`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-foreground-900">
                          {a.name}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">
                          代表作：{a.signatureRoles.join("、")}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="px-2 py-3 text-sm text-foreground-400">
              没有找到与“{query.trim()}”相关的内容
            </p>
          )}
        </div>
      )}
    </div>
  );
}